import * as React from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import { Checkbox, Button, Space, Spin } from 'antd';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { useCreation } from 'ahooks';
import { ModalIgnoreTime } from '../consts/modalIgnoreTime';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import XiaoLe from '../assets/person.png';
import { useSize } from 'ahooks';
import { useNavigate } from 'react-router-dom';
import { useOrgValue } from 'hooks/business/system/useOrg';
import useLayerLock from '../../../hooks/useLayerLock';
import { useEffect } from 'react';
import { atom, useSetAtom } from 'jotai';
import { useBootGuidePermission } from '../permission';
import posthog from '@leyan/lytics';
import useLLM from '../hooks/useLLM';
import { useTransAppProductName } from '../../../hooks/useTrans.ts';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries/index.ts';

const storeKeyPrefix = '@bootguide:feature:';
export const featureExistAtom = atom(false);

const eventReport = (leave: 'close' | 'ignore_7' | 'config') => {
  posthog.capture('$bootGuideFeature', {
    leave,
    eventName: '$bootGuideFeature',
  });
};

// 老用户新功能7天无理由弹窗
export const BootFeatureModal = React.memo(() => {
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const { gained, release, acquire } = useLayerLock('bootguide', 200);
  const { isDoneFirstConfigBootstrap } = useOrgConfigStateValue();
  const hasPermission = useBootGuidePermission();
  // const isAfter = useIsAfterBootFeatureOrg();
  const ignoredRef = React.useRef(localStorage.getItem(storeKeyPrefix + 'visible'));
  const imgRef = React.useRef<HTMLImageElement>(null);
  const imgRefWidth = useSize(imgRef)?.width || 0;
  const { whiteListConfig: { BOOTGUIDE = false } = {} } = useOrgConfigStateValue();
  const LLM = useLLM();
  const orgValue = useOrgValue();
  const isExpired = orgValue?.isExpired;

  const inprogressEnabled = useCreation(() => {
    const value = ignoredRef.current;
    if (!value) {
      return true;
    }
    if (new Date().getTime() > Number(value) && !isDoneFirstConfigBootstrap) {
      localStorage.removeItem(storeKeyPrefix + 'visible');
      return true;
    }
    return false;
  }, []);

  const [visible, setVisible] = React.useState(inprogressEnabled);

  const { data, isLoading: loading } = useQuery({
    ...queries.bootstraps.inprogress(LLM),
    enabled: !isExpired && inprogressEnabled,
  });

  const { data: data2, isLoading: loading2 } = useQuery({
    ...queries.notices.leftTopNotice(LLM),
    enabled: !isExpired,
  });

  const nav = useNavigate();
  const setFeatureExist = useSetAtom(featureExistAtom);

  const { unConfigAdvancedCount, unConfigAIModelCount, unConfigBasicCount } =
    data2?.configureBootstrap || {
      unConfigAdvancedCount: 0,
      unConfigAIModelCount: 0,
      unConfigBasicCount: 0,
    };

  const shouldShow =
    unConfigAdvancedCount > 0 || unConfigAIModelCount > 0 || unConfigBasicCount > 0;

  const actualShow = visible && shouldShow && BOOTGUIDE && hasPermission;

  useEffect(() => {
    if (!actualShow) {
      return release();
    }

    acquire();
    return () => release();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualShow]);
  const day7Ref = React.useRef(false);

  if (isExpired) return null;

  return (
    <JustRightHeightModal
      centered={true}
      visible={actualShow && gained}
      onCancel={() => {
        eventReport(day7Ref.current ? 'ignore_7' : 'close');
        setVisible(false);
      }}
      className={css`
        .ant-modal-body {
          padding: 0;
          margin: 0 !important;
          overflow: visible !important;
        }
      `}
      width="auto"
      footer={
        <div className="flex justify-between items-center">
          <Checkbox
            defaultChecked={false}
            onChange={(e) => {
              if (e.target.checked) {
                localStorage.setItem(
                  storeKeyPrefix + 'visible',
                  String(new Date().getTime() + ModalIgnoreTime),
                );
                day7Ref.current = true;
              } else {
                localStorage.removeItem(storeKeyPrefix + 'visible');
                day7Ref.current = false;
              }
            }}
          >
            {t('ignore_in_7_days')}
          </Checkbox>
          <div className="flex">
            <Space size={10}>
              <Button
                type="text"
                className="!text-[var(--fett-text-quaternary)]"
                onClick={() => {
                  eventReport(day7Ref.current ? 'ignore_7' : 'close');
                  setVisible(false);
                }}
              >
                {t('setup_later')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  if (data?.length) {
                    const config = data[0];
                    nav(`/bootguide/${config.configType.toLowerCase()}/${config.stepName}`);
                  } else {
                    nav('/bootguide/index');
                  }
                  // 这里没电啊
                  setFeatureExist(true);
                  eventReport('config');
                  localStorage.removeItem(storeKeyPrefix + 'visible');
                  setVisible(false);
                }}
              >
                {t('forward_setup')}
              </Button>
            </Space>
          </div>
        </div>
      }
    >
      <div>
        <div className="relative">
          <div className="absolute top-[-56px] left-[22px] bottom-0">
            <img src={XiaoLe} className="h-full w-auto" ref={imgRef} alt="" />
          </div>
          <div
            className="pr-[38px] py-[30px] min-h-[170px] min-w-[560px] max-w-[560px] break-words leading-[20px] text-[var(--fett-text-primary)]"
            style={{
              paddingLeft: imgRefWidth + 34,
            }}
          >
            {loading || loading2 ? (
              <div className="h-[170px] flex justify-center items-center">
                <Spin />
              </div>
            ) : (
              <div>
                {(data?.length || 0) > 0
                  ? t('modal_tip_1')
                  : LLM
                  ? appProductNameT('modal_tip_2_copy')
                  : unConfigBasicCount > 0
                  ? appProductNameT('modal_tip_2_copy')
                  : unConfigAIModelCount > 0
                  ? [
                      t(
                        '您好，小乐已为您准备就绪了智能接待配置，包含发货天数、运输天数、发货前发照片等内容，助您提高买家满意度。',
                      ),
                      <br />,
                      t('这个配置可能需要花费您3分钟，请问您是否有时间进行配置吗?'),
                    ]
                  : unConfigAdvancedCount > 0
                  ? t('modal_tip_3')
                  : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </JustRightHeightModal>
  );
});

BootFeatureModal.displayName = 'BootFeatureModal';
